import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserData, login, signOut } from './authThunk';
import { getToken, removeToken, removeRefreshToken, setToken, setRefreshToken, getUserData, removeUserData, setUserData } from '../../utils/HelperFunctions';
import api from '../../services/api';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

const initialState = {
  token: getToken() || null,
  loading: false,
  tokenData: null,
  idToken: null,
  idTokenData: null,
  user: null,
  userData: JSON.parse(getUserData()) || null,
  error: null,
  loginInProgress: false,
  userLoading: false,
  language: '',
};

interface UserSettings {
  appLanguage?: string;
  timeFormat?: '12h' | '24h' | 'auto';
  useLocalTimezone?: boolean;
  pageSettings?: Record<string, any>;
  [key: string]: any;
}

interface User {
  password?: string; // Password is optional when fetching users
  public_key?: string; // Add public_key here as optional
  user_settings?: UserSettings;
  language?: string;
}

export const updateUser = createAsyncThunk<User, User>('auth/updateUser', async (body, { rejectWithValue }) => {
  try {
    const response = await api.put('/auth/update-user', body);
    return response.data; // Cast the response as a User object
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Error updating user'));
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      const { token, refreshToken, userData } = action.payload;
      state.token = token;
      // state.userData = userData;
      state.loading = false;
      state.error = null;
      setToken(token);
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }
      // setUserData(JSON.stringify(userData));
    },
    // Optimistically merge a single user_settings field into userData so the
    // change is reflected before the updateUser round-trip resolves. The
    // subsequent updateUser.fulfilled replaces userData with the server's
    // merged copy, so this is a presentation-only head start, not the source
    // of persistence.
    setUserSetting: (state, action: PayloadAction<{ key: keyof UserSettings; value: unknown }>) => {
      const { key, value } = action.payload;
      if (!state.userData) state.userData = {};
      state.userData.user_settings = {
        ...(state.userData.user_settings || {}),
        [key]: value,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signOut.fulfilled, (state) => {
        removeToken();
        removeRefreshToken();
        state.loading = false;
        state.userData = {};
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = null;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { token, tokenData, idToken, idTokenData } = action.payload;
        state.token = token;
        state.tokenData = tokenData;
        state.idToken = idToken;
        state.idTokenData = idTokenData;
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        const { ...userData } = action.payload;
        // state.token = token;
        // state.tokenData = tokenData;
        // state.idToken = idToken;
        // state.idTokenData = idTokenData;
        state.userData = userData;
        state.loading = false;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.userData = {};
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = action.error.message;
      })
      .addCase(updateUser.pending, (state) => {
        state.userLoading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.userLoading = false;
      })
      .addCase(updateUser.rejected, (state) => {
        state.userLoading = false;
      });
  },
});

export const { loginSuccess, setUserSetting } = authSlice.actions;

export default authSlice.reducer;
